import React from 'react';
import styled from 'styled-components';
import { Breakpoints, Grid, Type } from '../../../app/StyleGuide';
import { RaisedContainer } from '../../../components/Layout';
import AssessmentReportScoreSegmentBar from './AssessmentReportScoreSegmentBar';
import { getScoreLevel, SCORE_LEVEL_LABELS } from '../utils/scoreUtils';

const SectionContainer = styled(RaisedContainer)`
  width: 100%;
  padding: ${Grid._6};
`;

const SectionHeader = styled.div`
  margin-bottom: ${Grid._6};

  h3 {
    margin: 0 0 ${Grid._3};
    font-size: ${Type.Scale._4};
    font-weight: ${Type.Weight.bold};
    color: ${Type.Color.dark};
  }
`;

const Summary = styled.p`
  margin: 0;
  font-size: ${Type.Scale._2};
  font-weight: ${Type.Weight.normal};
  color: ${Type.Color.medium};
  line-height: ${Type.Leading.taller};
`;

const ScoreBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Grid._6};

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    flex-direction: row;
    align-items: center;
    gap: ${Grid._8};
  }
`;

const ScoreColumn = styled.div`
  flex-shrink: 0;
`;

const ScoreValueRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${Grid._1};
  margin-bottom: ${Grid._2};
`;

const ScoreValue = styled.span`
  font-size: ${Type.Scale._8};
  font-weight: ${Type.Weight.bold};
  color: ${Type.Color.dark};
  line-height: 1;
`;

const ScoreOutOf = styled.span`
  font-size: ${Type.Scale._5};
  font-weight: ${Type.Weight.normal};
  color: ${Type.Color.medium};
  line-height: 1;
  padding-bottom: ${Grid._1};
`;

const EvidenceLabel = styled.p`
  margin: 0;
  font-size: ${Type.Scale._2};
  font-weight: ${Type.Weight.normal};
  color: ${Type.Color.medium};
  line-height: ${Type.Leading.tall};
`;

const BarColumn = styled.div`
  flex: 1;
  min-width: 0;
  width: 100%;
`;

const ScaleMarkers = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${Grid._2};
  font-size: ${Type.Scale._1};
  color: ${Type.Color.medium};
`;

type AssessmentReportOverallScoreProps = {
  score: number;
  summary: string;
};

export const AssessmentReportOverallScore = ({ score, summary }: AssessmentReportOverallScoreProps) => {
  const scoreLevel = getScoreLevel(score);

  return (
    <SectionContainer>
      <SectionHeader>
        <h3>Overall Assessment Score</h3>
        <Summary>{summary}</Summary>
      </SectionHeader>
      <ScoreBody>
        <ScoreColumn>
          <ScoreValueRow>
            <ScoreValue>{score.toFixed(2)}</ScoreValue>
            <ScoreOutOf>/5</ScoreOutOf>
          </ScoreValueRow>
          <EvidenceLabel>{SCORE_LEVEL_LABELS[scoreLevel]}</EvidenceLabel>
        </ScoreColumn>
        <BarColumn>
          <AssessmentReportScoreSegmentBar score={score} />
          <ScaleMarkers aria-hidden="true">
            <span>1</span>
            <span>3</span>
            <span>5</span>
          </ScaleMarkers>
        </BarColumn>
      </ScoreBody>
    </SectionContainer>
  );
};

export default AssessmentReportOverallScore;
